import React, { useRef, useState } from 'react';
import { validateEnrollmentCode, completeEnrollment } from '../services/enrollmentService';
import '../styles/Modal.css';

interface EnrollmentModalProps {
  onComplete: (identity: { name?: string; email?: string | null }) => void;
  onDismiss: () => void;
}

/**
 * Setup is a confirmation, not a silent save: the employee sees whose account
 * the code belongs to before anything is stored, so a mistyped code is caught
 * here rather than quietly mis-filing their time.
 *
 * "Not now" always works. Clocking in must never depend on this.
 */
const EnrollmentModal: React.FC<EnrollmentModalProps> = ({ onComplete, onDismiss }) => {
  const [code, setCode] = useState('');
  const [employeeName, setEmployeeName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Staleness guard for handleConfirm. "No, change the code" lets the
  // employee back out to the entry screen while completeEnrollment is still
  // in flight for the identity they just rejected — the modal stays mounted
  // (only its internal screen changes), so that request is still alive and
  // will eventually resolve. Without this, a late `{ ok: true }` would call
  // onComplete() and silently enroll the rejected identity, and a late
  // `{ ok: false }` would paint its error onto the entry screen the employee
  // is now retyping a *different* code into. Every completion attempt gets a
  // ticket; backing out invalidates it, and the continuation checks the
  // ticket is still current before touching state or calling onComplete.
  const confirmRequestId = useRef(0);

  const handleCheck = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);

    const result = await validateEnrollmentCode(code);
    setBusy(false);

    if (!result.ok) {
      setError(result.error || 'That code was not recognized.');
      return;
    }
    setEmployeeName(result.employeeName || 'this employee');
  };

  const handleConfirm = async () => {
    const requestId = ++confirmRequestId.current;
    setBusy(true);
    setError(null);

    const result = await completeEnrollment(code);

    // Superseded by "No, change the code" while this was in flight. The
    // employee already rejected this identity — do not enroll them, show
    // its error, or touch busy (that was already reset on back-out).
    if (confirmRequestId.current !== requestId) return;

    setBusy(false);

    if (!result.ok) {
      setError(result.error || 'Setup could not be completed.');
      return;
    }
    onComplete({ name: result.employeeName, email: result.employeeEmail });
  };

  const handleBack = () => {
    // Invalidate any in-flight completeEnrollment for the identity being
    // abandoned, and clear busy immediately so the entry screen's Continue
    // button isn't left disabled/"Checking…" until that stale request
    // eventually settles.
    confirmRequestId.current++;
    setBusy(false);
    setEmployeeName(null);
    setError(null);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>Set up this device</h2>

        {employeeName === null ? (
          <>
            <p>Enter the code the office gave you. You only need to do this once on this phone.</p>
            <form onSubmit={handleCheck}>
              <div className="form-group">
                <label htmlFor="enrollment-code">Enrollment code:</label>
                <input
                  type="text"
                  id="enrollment-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  autoFocus
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder="K7F-2QM"
                />
              </div>

              {error && <p style={{ color: '#b91c1c' }}>{error}</p>}

              {/*
                "Not now" is deliberately NEVER disabled — not while a
                validation request is in flight, not after an error, not
                offline. It is the one guaranteed exit back to the timeclock,
                so it cannot be gated on `busy` or any other state.
              */}
              <div className="modal-buttons">
                <button type="submit" className="save-btn" disabled={busy || !code.trim()}>
                  {busy ? 'Checking…' : 'Continue'}
                </button>
                <button type="button" onClick={onDismiss}>
                  Not now
                </button>
              </div>
            </form>
          </>
        ) : (
          <>
            <p>You&apos;re set up as:</p>
            <p style={{ fontSize: '1.25rem', fontWeight: 600 }}>{employeeName}</p>
            <p>Is that right?</p>

            {error && <p style={{ color: '#b91c1c' }}>{error}</p>}

            {/*
              Same rule as above: the confirmation step can also hang (a slow
              or dropped completeEnrollment call), so both the "go back" and
              "not now" escapes here stay enabled through `busy` too — an
              employee must never be trapped on this screen waiting on the
              network before they can clock in.
            */}
            <div className="modal-buttons">
              <button type="button" className="save-btn" onClick={handleConfirm} disabled={busy}>
                {busy ? 'Setting up…' : "Yes, that's me"}
              </button>
              <button
                type="button"
                onClick={handleBack}
              >
                No, change the code
              </button>
              <button type="button" onClick={onDismiss}>
                Not now
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EnrollmentModal;
